import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { resolveAssetUrl } from "@/lib/api";
import { X } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const Gallery = () => {
  useSEO({
    title: "Gallery",
    description: "Travel photographs from Shaurya Travels — landscapes, monuments, and unforgettable moments from across India and beyond.",
    path: "/gallery",
  });
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/photos")
      .then((r) => setPhotos(r.data || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="gallery-page">
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=2000&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 h-full flex flex-col justify-end pb-14 text-white">
          <p className="text-xs uppercase tracking-[0.4em] opacity-80 mb-4 fade-up">
            — Photographs
          </p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.0] fade-up-2">
            The Gallery
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg opacity-90 fade-up-3 font-light">
            A curated collection of moments captured on our journeys. Click any
            frame to view it larger.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-20 pb-16">
        {loading ? (
          <div data-testid="gallery-loading" className="text-center py-24 text-sm uppercase tracking-[0.25em] text-[var(--st-ink-2)]">
            Loading frames…
          </div>
        ) : photos.length === 0 ? (
          <div
            data-testid="gallery-empty"
            className="border border-dashed border-[var(--st-border)] py-32 text-center"
          >
            <p className="font-serif-display text-3xl mb-3">No photographs yet</p>
            <p className="text-sm text-[var(--st-ink-2)] mb-8">
              The gallery is waiting for its first stories.
            </p>
            <Link
              to="/admin/login"
              data-testid="gallery-empty-admin-link"
              className="inline-block px-6 py-3 text-xs uppercase tracking-[0.25em] btn-accent"
            >
              Sign in to upload
            </Link>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
            {photos.map((p) => (
              <figure
                key={p.id}
                data-testid={`gallery-photo-${p.id}`}
                className="masonry-col overflow-hidden bg-[var(--st-surface)] group cursor-zoom-in"
                onClick={() => setActive(p)}
              >
                <img
                  src={resolveAssetUrl(p.url)}
                  alt={p.caption || "Travel photograph"}
                  className="w-full h-auto object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                {p.caption && (
                  <figcaption className="px-1 py-3 text-xs uppercase tracking-[0.2em] text-[var(--st-ink-2)]">
                    {p.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        )}
      </section>

      {active && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setActive(null)}
        >
          <button
            data-testid="lightbox-close"
            className="absolute top-6 right-6 text-white/80 hover:text-white"
            onClick={() => setActive(null)}
          >
            <X size={28} />
          </button>
          <figure className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={resolveAssetUrl(active.url)}
              alt={active.caption || "Photograph"}
              className="w-full h-auto max-h-[85vh] object-contain"
            />
            {active.caption && (
              <figcaption className="text-white/80 text-center mt-4 text-sm tracking-wide">
                {active.caption}
              </figcaption>
            )}
          </figure>
        </div>
      )}
    </div>
  );
};

export default Gallery;
